import { __decorate, __metadata } from "tslib";
import { isPlatformServer } from '@angular/common';
import { DestroyRef, inject, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigService } from '@core/shared/config';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { getCurrentDomainInfo, takeWhileAlive } from '../utils';
import { DevToolsRegistry } from '../dev-tools';
import * as i0 from "@angular/core";
import * as i1 from "../dev-tools";
import * as i2 from "@core/shared/config";
import * as i3 from "@angular/router";
export var ScopeStateActions;
(function (ScopeStateActions) {
  let UpdateScope = /*#__PURE__*/(() => {
    class UpdateScope {
      static {
        this.type = '[ScopeState] UpdateScope';
      }
      constructor() {}
    }
    return UpdateScope;
  })();
  ScopeStateActions.UpdateScope = UpdateScope;
  // if app decides it uses the wrong scope, this will make sure to enable/redirect to correct scope
  let RedirectToScope = /*#__PURE__*/(() => {
    class RedirectToScope {
      static {
        this.type = '[ScopeState] RedirectToScope';
      }
      constructor(scope, path) {
        this.scope = scope;
        this.path = path;
      }
    }
    return RedirectToScope;
  })();
  ScopeStateActions.RedirectToScope = RedirectToScope;
  let SimulateScope = /*#__PURE__*/(() => {
    class SimulateScope {
      static {
        this.type = '[ScopeState] SimulateScope';
      }
      constructor(scope) {
        this.scope = scope;
      }
    }
    return SimulateScope;
  })();
  ScopeStateActions.SimulateScope = SimulateScope;
})(ScopeStateActions || (ScopeStateActions = {}));
export var ScopeMode = /*#__PURE__*/function (ScopeMode) {
  ScopeMode["subdomain"] = "subdomain";
  ScopeMode["virtual"] = "virtual";
  return ScopeMode;
}(ScopeMode || {});
const DEFAULT_SCOPE_ID = 'default';
let ScopeState = /*#__PURE__*/(() => {
  let ScopeState = class ScopeState {
    static scope(state) {
      return state.simulatedScope || state.scope;
    }
    static domainInfo(state) {
      return state.domainInfo;
    }
    constructor(ngPlatformId, document, debug, config, router) {
      this.ngPlatformId = ngPlatformId;
      this.document = document;
      this.debug = debug;
      this.config = config;
      this.router = router;
      this.destroyRef = inject(DestroyRef);
      const configuratedScopeMode = config.get('scope.mode', 'subdomain');
      const supportedModes = Object.values(ScopeMode);
      if (!supportedModes.includes(configuratedScopeMode)) {
        throw new Error('Unsupported scope mode configurated: "' + configuratedScopeMode + '"');
      }
      this.scopeMode = configuratedScopeMode;
    }
    ngxsOnInit(ctx) {
      ctx.dispatch(new ScopeStateActions.UpdateScope());
      this.debug.exposeDebugFunction(['Scope', 'Inspect current'], () => {
        return ctx.getState();
      });
      this.debug.defineDevSetting({
        id: 'enableSimulateScope',
        fieldType: 'checkbox',
        defaultValue: false,
        persist: true
      });
      this.debug.defineDevSetting({
        id: 'simulatedScopeName',
        fieldType: 'text',
        defaultValue: '',
        persist: true
      });
      this.debug.subscribeToDevSettings(['enableSimulateScope', 'simulatedScopeName']).pipe(takeWhileAlive(this.destroyRef)).subscribe(values => {
        if (!values.enableSimulateScope) {
          ctx.dispatch(new ScopeStateActions.SimulateScope(null));
        } else {
          const name = values.simulatedScopeName;
          ctx.dispatch(new ScopeStateActions.SimulateScope(name?.length ? name : null));
        }
      });
    }
    updateScope(ctx) {
      const isSSR = isPlatformServer(this.ngPlatformId);
      if (isSSR) {
        // for SSR this is enough as SSR will only run for main domain which we want to be SEO optimized.
        ctx.patchState({
          scope: DEFAULT_SCOPE_ID
        });
      } else {
        const domainInfo = getCurrentDomainInfo(this.document.location.href);
        const scope = this.getScopeFromDomainInfo(domainInfo);
        ctx.patchState({
          domainInfo,
          scope
        });
      }
    }
    simulateScope(ctx, action) {
      ctx.patchState({
        simulatedScope: action.scope
      });
    }
    redirectToScope(ctx, action) {
      if (ctx.getState().scope === action.scope) return;
      if (this.scopeMode === ScopeMode.subdomain) {
        // subdomain scope, redirect
        if (this.isLocalhost()) {
          throw new Error('Cannot redirect to scope subdomain while using localhost!');
        }
        const info = getCurrentDomainInfo(this.document.location.href);
        const hostname = `${action.scope}.${info.domain}.${info.topleveldomain}`;
        if (!action.path) {
          window.location.hostname = hostname;
        } else {
          const port = window.location.port ? ':' + window.location.port : '';
          const href = `${window.location.protocol}//${hostname}${port}/${action.path}`;
          window.location.href = href;
        }
      } else {
        // virtual scope, just update state
        ctx.patchState({
          simulatedScope: action.scope
        });
        if (action.path) this.router.navigate(['/' + action.path]);
      }
    }
    isLocalhost() {
      return this.document.location.href.includes('localhost');
    }
    getScopeFromDomainInfo(info) {
      return info.subdomain || DEFAULT_SCOPE_ID;
    }
    static {
      this.ɵfac = function ScopeState_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ScopeState)(i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(i1.DevToolsRegistry), i0.ɵɵinject(i2.ConfigService), i0.ɵɵinject(i3.Router));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: ScopeState,
        factory: ScopeState.ɵfac
      });
    }
  };
  __decorate([Action(ScopeStateActions.UpdateScope), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], ScopeState.prototype, "updateScope", null);
  __decorate([Action(ScopeStateActions.SimulateScope), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, ScopeStateActions.SimulateScope]), __metadata("design:returntype", void 0)], ScopeState.prototype, "simulateScope", null);
  __decorate([Action(ScopeStateActions.RedirectToScope), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, ScopeStateActions.RedirectToScope]), __metadata("design:returntype", void 0)], ScopeState.prototype, "redirectToScope", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], ScopeState, "scope", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], ScopeState, "domainInfo", null);
  ScopeState = __decorate([State({
    name: 'scope',
    defaults: {
      domainInfo: null,
      scope: DEFAULT_SCOPE_ID,
      simulatedScope: null
    }
  }), __metadata("design:paramtypes", [Object, Object, DevToolsRegistry, ConfigService, Router])], ScopeState);
  return ScopeState;
})();
export { ScopeState };